import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Key,
  Copy,
  CheckCircle,
  PlayCircle,
  Clock,
  AlertTriangle,
  Video,
  Globe,
  Webhook,
  Code
} from "lucide-react";
import Layout from "@/components/Layout";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const ApiGettingStarted = () => {
  const { t } = useTranslation();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const codeExamples = {
    auth: `Authorization: API-Key live-key-YOUR_API_KEY_HERE`,
    createProject: `curl --request POST \\
  --url https://api.autoclipper.live/api/v1/project \\
  --header 'Authorization: API-Key YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "base_video_url": "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    "webhook_url": "https://your-webhook-url.com/callback",
    "name": "My First Project",
    "format": "auto",
    "language": "en-US",
    "prompt": "Create engaging clips about the main topics"
  }'`,
    response: `{
  "id": "abc123def456",
  "name": "My First Project",
  "format": "auto",
  "language": "en-US",
  "process_status": "processing",
  "base_video_url": "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
  "webhook_url": "https://your-webhook-url.com/callback",
  "thumbnail_url": null,
  "clips": [],
  "createdAt": "2025-01-15T10:30:00.000Z",
  "updatedAt": "2025-01-15T10:30:00.000Z"
}`,
    checkStatus: `curl --request GET \\
  --url https://api.autoclipper.live/api/v1/project/abc123def456 \\
  --header 'Authorization: API-Key YOUR_API_KEY'`,
    completedResponse: `{
  "id": "abc123def456",
  "name": "My First Project",
  "process_status": "completed",
  "clips": [
    {
      "id": "clip_001",
      "name": "Main Topic Highlight",
      "rendered_video_url": "https://storage.autoclipper.live/clip_001_final.mp4"
    }
  ],
  "createdAt": "2025-01-15T10:30:00.000Z",
  "updatedAt": "2025-01-15T10:35:00.000Z"
}`
  };

  const webhookExample = `{
  "id": "mdzq28deb1aghlrnvz1rmrcz",
  "name": "Projeto 01",
  "format": "auto",
  "language": "pt-BR",
  "prompt": "",
  "base_video_url": "https://www.youtube.com/watch?v=W7dB7uJOD-k&pp=ygUKY29ydGUgZmxvdw%3D%3D",
  "webhook_url": "https://webhook.site/463a87db-9415-41bb-862e-eba1063a88d8",
  "process_status": "completed",
  "thumbnail_url": "https://storage.autoclipper.live/fj2wtw0qq0liu3cjpg1dwihc-video-thumbnail.jpg",
  "clips": [
    {
      "id": "delma233vetntmlkkr02pg5c",
      "name": "🎮 Negative Edge: O Segredo dos Jogos de Luta Explicado! 🥊",
      "rendered_video_url": "https://storage.autoclipper.live/33b91ee7a8c-rendered.mp4"
    },
    {
      "id": "p4au418clxdg54gpag9sm37n",
      "name": "🎮 Técnicas e Estratégias Avançadas em Jogos de Luta 🕹️",
      "rendered_video_url": "https://storage.autoclipper.live/3b91ee7a8ca-rendered.mp4"
    },
    {
      "id": "upt6hf39elsx1e3h6wkfji46",
      "name": "🕹️ A jogada IMPOSSÍVEL no Street Fighter 3! 🤯",
      "rendered_video_url": "https://storage.autoclipper.live/b91ee7a8ca7-rendered.mp4"
    }
  ],
  "createdAt": "2025-08-22T15:19:38.034Z",
  "updatedAt": "2025-08-22T15:22:31.461Z"
}`;

  const prerequisites = [
    {
      icon: Key,
      title: t('apiGettingStarted.apiKey'),
      description: t('apiGettingStarted.apiKeyDescription'),
      action: (
        <Button asChild variant="outline" size="sm">
          <Link to="/api-keys">{t('apiGettingStarted.getApiKey')}</Link>
        </Button>
      )
    },
    {
      icon: Video,
      title: t('apiGettingStarted.videoContent'),
      description: t('apiGettingStarted.videoContentDescription'),
      action: null
    },
    {
      icon: Globe,
      title: t('apiGettingStarted.httpClient'),
      description: t('apiGettingStarted.httpClientDescription'),
      action: null
    }
  ];

  const formatOptions = [
    {
      value: "auto",
      label: t('apiGettingStarted.formatAuto'),
      description: t('apiGettingStarted.formatAutoDescription')
    },
    {
      value: "max_1_min",
      label: t('apiGettingStarted.formatMax1Min'),
      description: t('apiGettingStarted.formatMax1MinDescription')
    },
    {
      value: "max_30_sec",
      label: t('apiGettingStarted.formatMax30Sec'),
      description: t('apiGettingStarted.formatMax30SecDescription')
    }
  ];

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="sm">
            <Link to="/api-documentation">
              <ArrowLeft className="w-4 h-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{t('apiGettingStarted.title')}</h1>
            <p className="text-muted-foreground">
              {t('apiGettingStarted.subtitle')}
            </p>
          </div>
        </div>

        {/* Prerequisites */}
        <Card>
          <CardHeader>
            <CardTitle>{t('apiGettingStarted.prerequisites')}</CardTitle>
            <CardDescription>
              {t('apiGettingStarted.prerequisitesDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              {prerequisites.map((prereq) => (
                <div key={prereq.title} className="flex flex-col gap-3 p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center">
                      <prereq.icon className="w-4 h-4 text-cyan-600" />
                    </div>
                    <h3 className="font-semibold">{prereq.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{prereq.description}</p>
                  {prereq.action}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Step 1: Authentication */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center text-sm font-semibold">1</div>
              {t('apiGettingStarted.authentication')}
            </CardTitle>
            <CardDescription>
              {t('apiGettingStarted.authDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm">
              {t('apiGettingStarted.authInstructions')}
            </p>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Badge variant="outline">{t('apiGettingStarted.authHeader')}</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(codeExamples.auth, 'auth')}
                >
                  {copiedCode === 'auth' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <pre className="bg-muted p-3 rounded-lg text-sm">
                <code>{codeExamples.auth}</code>
              </pre>
            </div>

            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {t('apiGettingStarted.securityWarning')}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Step 2: Create Project */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center text-sm font-semibold">2</div>
              {t('apiGettingStarted.createFirstProject')}
            </CardTitle>
            <CardDescription>
              {t('apiGettingStarted.createFirstProjectDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm">
              {t('apiGettingStarted.createProjectInstructions')}
            </p>

            {/* Parameters */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">{t('apiGettingStarted.requestParameters')}</h4>
              <div className="grid gap-2 text-sm">
                <div className="flex gap-2">
                  <Badge variant="default">{t('apiGettingStarted.required')}</Badge>
                  <code>base_video_url</code> - {t('apiGettingStarted.paramBaseVideoUrl')}
                </div>
                <div className="flex gap-2">
                  <Badge variant="secondary">{t('apiGettingStarted.optional')}</Badge>
                  <code>name</code> - {t('apiGettingStarted.paramName')}
                </div>
                <div className="flex gap-2">
                  <Badge variant="default">{t('apiGettingStarted.required')}</Badge>
                  <code>format</code> - {t('apiGettingStarted.paramFormat')}
                </div>
                <div className="flex gap-2">
                  <Badge variant="default">{t('apiGettingStarted.required')}</Badge>
                  <code>language</code> - {t('apiGettingStarted.paramLanguage')}
                </div>
                <div className="flex gap-2">
                  <Badge variant="secondary">{t('apiGettingStarted.optional')}</Badge>
                  <code>webhook_url</code> - {t('apiGettingStarted.paramWebhookUrl')}
                </div>
                <div className="flex gap-2">
                  <Badge variant="secondary">{t('apiGettingStarted.optional')}</Badge>
                  <code>prompt</code> - {t('apiGettingStarted.paramPrompt')}
                </div>
                <div className="flex gap-2">
                  <Badge variant="secondary">{t('apiGettingStarted.optional')}</Badge>
                  <code>brand_kit_id</code> - {t('apiGettingStarted.paramBrandKitId')}
                </div>
              </div>
            </div>

            {/* Format Options */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">{t('apiGettingStarted.formatOptions')}</h4>
              <div className="grid gap-2 md:grid-cols-2">
                {formatOptions.map((format) => (
                  <div key={format.value} className="p-3 border rounded-lg">
                    <div className="font-medium text-sm">{format.label}</div>
                    <div className="text-xs text-muted-foreground">{format.description}</div>
                    <code className="text-xs bg-muted px-1 py-0.5 rounded">{format.value}</code>
                  </div>
                ))}
              </div>
            </div>

            {/* Example Request */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Badge variant="outline">{t('apiGettingStarted.exampleRequest')}</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(codeExamples.createProject, 'create')}
                >
                  {copiedCode === 'create' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <pre className="bg-muted p-3 rounded-lg text-sm overflow-x-auto">
                <code>{codeExamples.createProject}</code>
              </pre>
            </div>

            {/* Example Response */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Badge variant="outline">{t('apiGettingStarted.exampleResponse')}</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(codeExamples.response, 'response')}
                >
                  {copiedCode === 'response' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <pre className="bg-muted p-3 rounded-lg text-sm overflow-x-auto">
                <code>{codeExamples.response}</code>
              </pre>
            </div>
          </CardContent>
        </Card>

        {/* Step 3: Monitor Progress */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center text-sm font-semibold">3</div>
              {t('apiGettingStarted.monitorProgress')}
            </CardTitle>
            <CardDescription>
              {t('apiGettingStarted.monitorProgressDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm">
              {t('apiGettingStarted.monitorInstructions')}
            </p>

            {/* Status Check Request */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Badge variant="outline">{t('apiGettingStarted.checkStatus')}</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(codeExamples.checkStatus, 'status')}
                >
                  {copiedCode === 'status' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <pre className="bg-muted p-3 rounded-lg text-sm overflow-x-auto">
                <code>{codeExamples.checkStatus}</code>
              </pre>
            </div>

            {/* Processing Status Values */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">{t('apiGettingStarted.statusValues')}</h4>
              <div className="grid gap-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                    <Clock className="w-3 h-3 mr-1" />
                    processing
                  </Badge>
                  <span className="text-sm">{t('apiGettingStarted.statusProcessing')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    completed
                  </Badge>
                  <span className="text-sm">{t('apiGettingStarted.statusCompleted')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    failed
                  </Badge>
                  <span className="text-sm">{t('apiGettingStarted.statusFailed')}</span>
                </div>
              </div>
            </div>

            {/* Completed Response */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Badge variant="outline">{t('apiGettingStarted.responseWhenCompleted')}</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(codeExamples.completedResponse, 'completed')}
                >
                  {copiedCode === 'completed' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <pre className="bg-muted p-3 rounded-lg text-sm overflow-x-auto">
                <code>{codeExamples.completedResponse}</code>
              </pre>
            </div>

            <Alert>
              <Webhook className="h-4 w-4" />
              <AlertDescription>
                {t('apiGettingStarted.proTip')}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Webhook Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Webhook className="w-5 h-5" />
              {t('apiGettingStarted.understandingWebhooks')}
            </CardTitle>
            <CardDescription>
              {t('apiGettingStarted.webhookDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm">
              {t('apiGettingStarted.webhookInstructions')}
            </p>

            <div className="space-y-3">
              <h4 className="font-semibold text-sm">{t('apiGettingStarted.webhookDetailsTitle')}</h4>
              <div className="grid gap-2 text-sm">
                <div className="flex gap-2">
                  <Badge variant="default">{t('apiGettingStarted.method')}</Badge>
                  <span>{t('apiGettingStarted.methodDescription')}</span>
                </div>
                <div className="flex gap-2">
                  <Badge variant="default">{t('apiGettingStarted.contentType')}</Badge>
                  <span>application/json</span>
                </div>
                <div className="flex gap-2">
                  <Badge variant="default">{t('apiGettingStarted.success')}</Badge>
                  <span>{t('apiGettingStarted.successDescription')}</span>
                </div>
                <div className="flex gap-2">
                  <Badge variant="destructive">{t('apiGettingStarted.failure')}</Badge>
                  <span>{t('apiGettingStarted.failureDescription')}</span>
                </div>
                <div className="flex gap-2">
                  <Badge variant="default">{t('apiGettingStarted.retries')}</Badge>
                  <span>{t('apiGettingStarted.retriesDescription')}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Badge variant="outline">{t('apiGettingStarted.successPayloadExample')}</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(webhookExample, 'webhook')}
                >
                  {copiedCode === 'webhook' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <pre className="bg-muted p-3 rounded-lg text-sm overflow-x-auto">
                <code>{webhookExample}</code>
              </pre>
            </div>

            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {t('apiGettingStarted.webhookImportant')}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card>
          <CardHeader>
            <CardTitle>{t('apiGettingStarted.whatsNext')}</CardTitle>
            <CardDescription>
              {t('apiGettingStarted.whatsNextDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <Button asChild variant="outline" className="h-auto p-4 justify-start">
                <Link to="/api-playground">
                  <div className="flex items-start gap-3">
                    <Code className="w-5 h-5 mt-0.5" />
                    <div className="text-left">
                      <div className="font-medium">{t('apiGettingStarted.tryPlayground')}</div>
                      <div className="text-sm text-muted-foreground">{t('apiGettingStarted.tryPlaygroundDescription')}</div>
                    </div>
                  </div>
                </Link>
              </Button>

              <Button asChild variant="outline" className="h-auto p-4 justify-start">
                <Link to="/api-reference">
                  <div className="flex items-start gap-3">
                    <PlayCircle className="w-5 h-5 mt-0.5" />
                    <div className="text-left">
                      <div className="font-medium">{t('apiGettingStarted.exploreReference')}</div>
                      <div className="text-sm text-muted-foreground">{t('apiGettingStarted.exploreReferenceDescription')}</div>
                    </div>
                  </div>
                </Link>
              </Button>

              <Button asChild variant="outline" className="h-auto p-4 justify-start">
                <Link to="/api-requests">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 mt-0.5" />
                    <div className="text-left">
                      <div className="font-medium">{t('apiGettingStarted.monitorUsage')}</div>
                      <div className="text-sm text-muted-foreground">{t('apiGettingStarted.monitorUsageDescription')}</div>
                    </div>
                  </div>
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ApiGettingStarted;